// src/hooks/useStats.js
import { useEffect, useState, useRef } from "react";
import { collection, query, where, onSnapshot, getFirestore } from "firebase/firestore";
import { db as exportedDb } from "../firebase"; // keep using whatever your firebase file exports

export default function useStats() {
    // added aiTrips and per-metric loading flags
    const [stats, setStats] = useState({ users: 0, publicTrips: 0, aiTrips: 0 });
    const [loading, setLoading] = useState({ users: true, publicTrips: true, aiTrips: true });
    const mounted = useRef(true);

    // Resolve a usable Firestore instance (prefer the exported one, fall back to getFirestore())
    const getDb = () => {
        if (exportedDb) return exportedDb;
        try {
            const fallback = getFirestore(); // will use the default app
            console.warn("[useStats] Using fallback getFirestore() instance (exported db was falsy).");
            return fallback;
        } catch (err) {
            console.error("[useStats] No Firestore instance available:", err);
            return null;
        }
    };

    useEffect(() => {
        mounted.current = true;
        const db = getDb();
        if (!db) {
            // Fail gracefully: set loading false and leave counts at 0
            setLoading({ users: false, publicTrips: false, aiTrips: false });
            return;
        }

        // --- Users: count all docs in users collection.
        const usersCol = collection(db, "users");
        const unsubUsers = onSnapshot(
            usersCol,
            (snap) => {
                if (!mounted.current) return;
                setStats((s) => ({ ...s, users: snap.size }));
                setLoading((l) => ({ ...l, users: false }));
            },
            (err) => {
                console.error("[useStats] users onSnapshot error:", err);
                setStats((s) => ({ ...s, users: 0 }));
                setLoading((l) => ({ ...l, users: false }));
            }
        );

        // --- Public trips: prefer explicit visibility === 'public' filter.
        // If your schema uses a boolean public flag, replace the where clause accordingly.
        let unsubPublicTrips = () => { };
        try {
            const publicTripsQ = query(collection(db, "trips"), where("visibility", "==", "public"));
            unsubPublicTrips = onSnapshot(
                publicTripsQ,
                (snap) => {
                    if (!mounted.current) return;
                    setStats((s) => ({ ...s, publicTrips: snap.size }));
                    setLoading((l) => ({ ...l, publicTrips: false }));
                },
                (err) => {
                    console.error("[useStats] public trips onSnapshot (visibility) error:", err);
                    setStats((s) => ({ ...s, publicTrips: 0 }));
                    setLoading((l) => ({ ...l, publicTrips: false }));
                }
            );
        } catch (err) {
            // Fallback: count all trips if the visibility query fails
            console.warn("[useStats] falling back to full trips collection snapshot for publicTrips count:", err);
            const allTripsCol = collection(db, "trips");
            unsubPublicTrips = onSnapshot(
                allTripsCol,
                (snap) => {
                    if (!mounted.current) return;
                    setStats((s) => ({ ...s, publicTrips: snap.size }));
                    setLoading((l) => ({ ...l, publicTrips: false }));
                },
                (err2) => {
                    console.error("[useStats] public trips fallback onSnapshot error:", err2);
                    setStats((s) => ({ ...s, publicTrips: 0 }));
                    setLoading((l) => ({ ...l, publicTrips: false }));
                }
            );
        }

        // --- AI-generated trips: your project uses a separate `ai_trips` collection.
        // We listen to that collection directly for an accurate count.
        let unsubAiTrips = () => { };
        try {
            const aiTripsCol = collection(db, "ai_trips");
            unsubAiTrips = onSnapshot(
                aiTripsCol,
                (snap) => {
                    if (!mounted.current) return;
                    setStats((s) => ({ ...s, aiTrips: snap.size }));
                    setLoading((l) => ({ ...l, aiTrips: false }));
                },
                (err) => {
                    console.error("[useStats] ai_trips onSnapshot error:", err);
                    setStats((s) => ({ ...s, aiTrips: 0 }));
                    setLoading((l) => ({ ...l, aiTrips: false }));
                }
            );
        } catch (err) {
            // Very defensive fallback: if ai_trips isn't available, try scanning trips collection to infer flags.
            console.warn("[useStats] ai_trips collection unavailable; falling back to scanning 'trips' for AI flags:", err);
            const allTripsCol = collection(db, "trips");
            unsubAiTrips = onSnapshot(
                allTripsCol,
                (snap) => {
                    if (!mounted.current) return;
                    const aiCount = snap.docs.reduce((acc, d) => {
                        try {
                            const data = d.data();
                            // best-effort: check common field names
                            const isAi =
                                data?.aiGenerated === true ||
                                data?.generatedByAI === true ||
                                (data?.source && String(data.source).toLowerCase() === "ai");
                            return acc + (isAi ? 1 : 0);
                        } catch (e) {
                            return acc;
                        }
                    }, 0);
                    setStats((s) => ({ ...s, aiTrips: aiCount }));
                    setLoading((l) => ({ ...l, aiTrips: false }));
                },
                (err2) => {
                    console.error("[useStats] ai fallback onSnapshot error:", err2);
                    setStats((s) => ({ ...s, aiTrips: 0 }));
                    setLoading((l) => ({ ...l, aiTrips: false }));
                }
            );
        }

        // cleanup
        return () => {
            mounted.current = false;
            try { unsubUsers(); } catch (e) { /* noop */ }
            try { unsubPublicTrips(); } catch (e) { /* noop */ }
            try { unsubAiTrips(); } catch (e) { /* noop */ }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // run once on mount

    return { stats, loading };
}
